import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { getOrpc } from '@/_shared/lib/orpc';

import type { RpcDashboardSpec } from '../lib/to-rpc-spec';
import type { DashboardParam } from '../types';

interface ParamOption {
  label: string;
  value: string;
}

export interface PreviewParamOptionsSource {
  warehouseId: string;
  spec: RpcDashboardSpec;
}

/**
 * Fetches dynamic options for dashboard parameters that have a `sql` field.
 * Returns merged parameters with fetched options replacing/supplementing static ones.
 *
 * Merge rule: only replace `options` when fetched result is non-empty.
 * If SQL returns 0 rows, preserve the static `options` fallback.
 *
 * Accepts an `orpc` instance so both workspace-scoped and public routes
 * can share this hook with their own oRPC utils.
 */

export function useDynamicParamOptions(
  dashboardId: string | undefined,
  parameters: DashboardParam[],
  orpc: ReturnType<typeof getOrpc>,
  shareToken?: string,
  previewSource?: PreviewParamOptionsSource,
): { parameters: DashboardParam[]; isLoading: boolean; loadingParamNames: Set<string> } {
  const dynamicParams = useMemo(() => parameters.filter((p) => !!p.sql || p.hasDynamicOptions === true), [parameters]);

  const { dataByName, loadingNames, isLoading } = useQueries({
    queries: dynamicParams.map((p) => {
      if (previewSource) {
        return {
          ...orpc.dashboard.previewParamOptions.queryOptions({
            input: {
              warehouseId: previewSource.warehouseId,
              spec: {
                parameters: previewSource.spec.parameters,
                panels: [],
              },
              paramName: p.name,
            },
          }),
          enabled: true,
          staleTime: 5 * 60 * 1000,
        };
      }
      return {
        ...orpc.dashboard.paramOptions.queryOptions({
          input: { dashboardId: dashboardId ?? '', paramName: p.name, ...(shareToken ? { shareToken } : {}) },
        }),
        enabled: !!dashboardId,
        staleTime: 5 * 60 * 1000,
      };
    }),
    // `combine`'s output is structurally shared by react-query; without it useQueries returns a fresh array every render, thrashing the memos below.
    combine: (results) => {
      const dataByName: Record<string, ParamOption[]> = {};
      const loadingNames: string[] = [];
      let isLoading = false;
      for (let i = 0; i < results.length; i++) {
        const name = dynamicParams[i]?.name;
        if (!name) continue;
        const result = results[i]!;
        if (result.isLoading) {
          isLoading = true;
          loadingNames.push(name);
        }
        if (result.data?.options && result.data.options.length > 0) {
          dataByName[name] = result.data.options;
        }
      }
      return { dataByName, loadingNames, isLoading };
    },
  });

  const loadingParamNames = useMemo(() => new Set(loadingNames), [loadingNames]);

  const resolvedParams = useMemo(() => {
    if (dynamicParams.length === 0) return parameters;
    return parameters.map((p) => {
      const fetched = dataByName[p.name];
      return fetched ? { ...p, options: fetched } : p;
    });
  }, [parameters, dynamicParams, dataByName]);

  return { parameters: resolvedParams, isLoading, loadingParamNames };
}
